import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useInView } from "react-intersection-observer"
import { Pagination } from "@src/presentation/components/Pagination"
import { CircleCheck, Clock } from "lucide-react"

interface Pasco {
    pascoId: string
    courseCode: string
    courseName: string
    academicYear: string
    semester: number
    level: number
    isApproved: boolean
    isReviewed: boolean
    processingStatus: "pending" | "processing" | "completed" | "failed"
    createdAt: string
    uploadedBy?: {
        userId: string
        name: string
        profilePicture: string | null
    }
}

interface PascosTableProps {
    pascos: Pasco[]
    itemsPerPage?: number
    fetchNextPage?: () => void
    hasNextPage?: boolean
    isFetchingNextPage?: boolean
}

export function PascosTable({ pascos, itemsPerPage = 10, fetchNextPage, hasNextPage, isFetchingNextPage }: PascosTableProps) {
    const [currentPage, setCurrentPage] = useState(1)
    const navigate = useNavigate()

    // Intersection observer for infinite scroll
    const { ref, inView } = useInView()

    // Load more when in view (only for infinite scroll)
    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage && fetchNextPage) {
            fetchNextPage()
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

    // Determine if we're using infinite scroll or pagination
    const useInfiniteScroll = !!fetchNextPage
    const totalPages = Math.ceil(pascos.length / itemsPerPage)
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    const currentPascos = useInfiniteScroll ? pascos : pascos.slice(startIndex, endIndex)

    const handleRowClick = (pascoId: string) => {
        navigate(`/pascos/${pascoId}`)
    }



    const getSemesterText = (semester: number) => {
        switch (semester) {
            case 1: return "1st Sem";
            case 2: return "2nd Sem";
            case 3: return "3rd Sem";
            default: return `${semester} Sem`;
        }
    }

    return (
        <div className="rounded border border-border bg-card shadow-none hover:shadow-custom transition-shadow">
            <div className="border-b border-border p-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                            <svg className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                                />
                            </svg>
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-foreground">PASCOs</h2>
                            <p className="text-sm text-muted-foreground">{pascos.length} total PASCOs</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-accent">
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 4v16m8-8H4"
                                />
                            </svg>
                        </button>
                    </div>

                </div>
            </div>

            {/* Scrollable table container for infinite scroll */}
            <div className="max-h-[600px] overflow-y-auto">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="sticky top-0 bg-card z-10">
                            <tr className="border-b border-border bg-muted/50">
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                                    Course
                                </th>
                                <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                                    Academic Year
                                </th>
                                <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                                    Semester
                                </th>
                                <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                                    Level
                                </th>
                                <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                                    Uploaded By
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                                    Approval
                                </th>
                                <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                                    Review
                                </th>
                                {/* <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                                    Created
                                </th> */}
                                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {currentPascos.map((pasco) => (
                                <tr
                                    key={pasco.pascoId}
                                    onClick={() => handleRowClick(pasco.pascoId)}
                                    className="transition-colors hover:bg-muted/50 cursor-pointer min-h-[64px]"
                                >
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col">
                                            <div className="flex items-center gap-2">
                                                <span className="rounded bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                                                    {pasco.courseCode}
                                                </span>
                                            </div>
                                            <div className="mt-1 max-w-xs text-sm font-medium text-foreground">{pasco.courseName}</div>
                                            <code className="mt-0.5 text-xs text-muted-foreground">{pasco.pascoId.slice(0, 8)}...</code>
                                        </div>
                                    </td>
                                    <td className="hidden md:table-cell whitespace-nowrap px-6 py-4 text-sm text-foreground">{pasco.academicYear}</td>
                                    <td className="hidden md:table-cell whitespace-nowrap px-6 py-4">
                                        <span className="text-sm text-foreground">{getSemesterText(pasco.semester)}</span>
                                    </td>
                                    <td className="hidden md:table-cell whitespace-nowrap px-6 py-4">
                                        <div className="flex items-center gap-1.5">
                                            <div className="flex h-6 w-6 items-center justify-center rounded bg-accent/10 text-xs font-semibold text-accent">
                                                {pasco.level / 100}
                                            </div>
                                            <span className="text-sm text-muted-foreground">Level {pasco.level}</span>
                                        </div>
                                    </td>
                                    <td className="hidden md:table-cell whitespace-nowrap px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            {pasco.uploadedBy ? (
                                                <>
                                                    {pasco.uploadedBy.profilePicture ? (
                                                        <img
                                                            src={pasco.uploadedBy.profilePicture}
                                                            alt={pasco.uploadedBy.name}
                                                            className="h-6 w-6 rounded-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                                                            {pasco.uploadedBy.name.charAt(0).toUpperCase()}
                                                        </div>
                                                    )}
                                                    <span className="text-sm text-foreground max-w-[120px] truncate">
                                                        {pasco.uploadedBy.name}
                                                    </span>
                                                </>
                                            ) : (
                                                <span className="text-sm text-muted-foreground">—</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="whitespace-nowrap px-6 py-4">
                                        {pasco.isApproved ? (
                                            <div className="flex items-center gap-1.5 text-success">
                                                <CircleCheck className="h-4 w-4" />
                                                <span className="text-xs font-medium hidden sm:block">Approved</span>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-1.5 text-warning">
                                                <Clock className="h-4 w-4" />
                                                <span className="text-xs font-medium hidden sm:block">Pending</span>
                                            </div>
                                        )}
                                    </td>
                                    <td className="hidden md:table-cell whitespace-nowrap px-6 py-4">
                                        {pasco.isReviewed ? (
                                            <div className="flex items-center gap-1.5 text-blue-500">
                                                <CircleCheck className="h-4 w-4" />
                                                <span className="text-xs font-medium">Reviewed</span>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-1.5 text-orange-500">
                                                <Clock className="h-4 w-4" />
                                                <span className="text-xs font-medium">Pending</span>
                                            </div>
                                        )}
                                    </td>
                                    {/* <td className="hidden md:table-cell whitespace-nowrap px-6 py-4 text-sm text-muted-foreground">
                                        {formatDate(pasco.createdAt)}
                                    </td> */}
                                    <td className="whitespace-nowrap px-6 py-4 text-right">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    navigate(`/pascos/${pasco.pascoId}`)
                                                }}
                                                className="rounded-lg border border-border bg-background p-1.5 text-foreground transition-colors hover:bg-accent"
                                                title="View details"
                                            >
                                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                                    />
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                                    />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    navigate(`/pascos/${pasco.pascoId}/edit`)
                                                }}
                                                className="rounded-lg border border-border bg-background p-1.5 text-foreground transition-colors hover:bg-accent"
                                                title="Edit"
                                            >
                                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                                    />
                                                </svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination or Infinite Scroll */}
                {useInfiniteScroll ? (
                    <>
                        {/* Infinite Scroll Trigger */}
                        {hasNextPage && (
                            <div ref={ref} className="flex items-center justify-center py-8">
                                {isFetchingNextPage ? (
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                                        Loading more PASCOs...
                                    </div>
                                ) : (
                                    <div className="h-4"></div>
                                )}
                            </div>
                        )}

                        {!hasNextPage && pascos.length > 0 && (
                            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                                You've reached the end
                            </div>
                        )}
                    </>
                ) : (
                    /* Pagination */
                    pascos.length > itemsPerPage && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalItems={pascos.length}
                            itemsPerPage={itemsPerPage}
                            onPageChange={setCurrentPage}
                        />
                    )
                )}

            </div>

            {pascos.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                    <svg
                        className="h-12 w-12 text-muted-foreground/50"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                        />
                    </svg>
                    <p className="mt-2 text-sm text-muted-foreground">No PASCOs found</p>
                </div>
            )}
        </div>
    )
}
